import { useState } from "react";
import { toast } from "sonner";
import { Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { daysLeft } from "@/components/billing/PlanLinks";
import { FEATURE_LABEL } from "@/components/billing/BuildPlan";
import { fmtDate, TIER, planName } from "@/components/billing/ChangePlanDialog";

export async function openPortal() {
  const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
  window.location.href = data.url;
}

export function CurrentPlan({ b, onChanged, extra = null }) {
  const [busy, setBusy] = useState(false);
  const sub = b.subscription;
  const toggle = async (resume) => {
    setBusy(true);
    try { await api.post(`/billing/cancel${resume ? "?resume=true" : ""}`); toast.success(resume ? "Your subscription will keep renewing" : "Your subscription won't renew"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sc = sub?.scheduled_change;
  const pending = !!(sc || sub?.cancel_at_period_end);
  const keep = async () => {
    setBusy(true);
    try { await api.post("/billing/keep-plan"); toast.success("You're staying on your current plan"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const portal = async () => { setBusy(true); try { await openPortal(); } catch (e) { toast.error(errMsg(e)); setBusy(false); } };
  const label = {
    event: `Free for everyone during ${b.event?.name} until ${fmtDate(b.event?.end)}`,
    trial: `Free trial · ${daysLeft(b.trial_ends_at)} (ends ${fmtDate(b.trial_ends_at)}). Subscribe any time to keep Pro.`,
    referral: `Free Pro earned by inviting friends · until ${fmtDate(b.bonus_pro_until)}`,
    admin: "Admin access", lifetime: `${TIER[b.lifetime] || "Pro"} for life · paid once, every future update included`, granted: b.features?.length < 4 ? `Granted by an admin: ${b.features.map((f) => FEATURE_LABEL[f]).join(", ")}` : `${TIER[b.tier] || "Pro"} granted by an admin`,
    subscription: `${sub?.features ? `Custom: ${sub.features.map((f) => FEATURE_LABEL[f]).join(", ")}${sub.tier === "commercial" ? " + commercial license" : ""}` : TIER[sub?.tier || "pro"]} · billed ${sub?.interval === "year" ? "yearly" : "monthly"}`,
  }[b.source];
  const title = b.source === "trial" ? "You're on a LoreRight Pro trial" : b.source === "event" ? `${b.event?.name}: Pro is free for everyone` : b.features?.length < 4 ? `You have a custom LoreRight plan${b.tier === "commercial" ? " with a commercial license" : ""}` : `You have LoreRight ${TIER[b.tier] || "Pro"}`;
  return (
    <div className="mb-10 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-6" data-testid="current-plan">
      <div className="flex items-center gap-2 text-[#FBBF24]"><Crown className="h-4 w-4" /><span className="font-medium">{title}</span></div>
      <p className="mt-1 text-sm text-[#A8A090]" data-testid="current-plan-source">{label}
        {b.source === "subscription" && sub?.current_period_end && ` · ${sub.cancel_at_period_end ? "ends" : "renews"} ${fmtDate(sub.current_period_end)}`}</p>
      {b.base_source === "subscription" && sc && <p className="mt-1 text-xs text-[#FBBF24]" data-testid="scheduled-change">Switching to {planName(sc)}, billed {sc.interval === "year" ? "yearly" : "monthly"}, on {fmtDate(sc.at)}. You keep your current plan until then.</p>}
      {b.base_source === "subscription" && sub?.cancel_at_period_end && <p className="mt-1 text-xs text-[#FBBF24]" data-testid="scheduled-free">Moving to Free on {fmtDate(sub.current_period_end)}. You keep your current plan until then.</p>}
      {b.event && b.source !== "event" && <p className="mt-1 text-xs text-[#FBBF24]" data-testid="current-plan-event">{b.event.name}: Pro is free for everyone until {fmtDate(b.event.end)}{b.base_source === "subscription" ? ". Your next charge has been pushed back to cover it." : "."}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {b.has_billing && <Button size="sm" disabled={busy} onClick={portal} data-testid="manage-billing-button">Manage billing</Button>}
        {b.base_source === "subscription" && pending && (
          <Button variant="outline" size="sm" disabled={busy} onClick={keep} data-testid="keep-plan-button">Keep my current plan</Button>
        )}
        {b.base_source === "subscription" && !pending && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => toggle(false)} data-testid="toggle-renewal-button">Cancel at period end</Button>
        )}
        {extra}
      </div>
    </div>
  );
}
